import { Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  distinctVoiceValues,
  voiceAccent,
  voiceGender,
  voiceStatusLabels,
  type SoundFilters,
} from "./catalogState";
import type { SoundVoice } from "./contracts";
import { SoundFilterSelect } from "./SoundFilterSelect";

const capitalize = (value: string) =>
  value.charAt(0).toLocaleUpperCase() + value.slice(1).replaceAll("_", " ");

export function SoundFiltersBar({
  voices,
  filters,
  onChange,
}: {
  voices: SoundVoice[];
  filters: SoundFilters;
  onChange: (filters: SoundFilters) => void;
}) {
  const genders = distinctVoiceValues(voices, voiceGender);
  const accents = distinctVoiceValues(voices, voiceAccent);
  const field = <Key extends keyof SoundFilters>(
    key: Key,
    value: SoundFilters[Key],
  ) => onChange({ ...filters, [key]: value });

  return (
    <div className="grid gap-3 rounded-lg border bg-muted/30 p-3 sm:grid-cols-2 xl:grid-cols-5">
      <div className="space-y-1 sm:col-span-2">
        <Label htmlFor="sound-search">Find a voice</Label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="sound-search"
            className="pl-8"
            value={filters.search}
            onChange={(event) => field("search", event.target.value)}
            placeholder="Search by name…"
          />
        </div>
      </div>
      <SoundFilterSelect
        id="sound-status"
        label="Status"
        value={filters.status}
        allLabel="All statuses"
        options={Object.entries(voiceStatusLabels).map(([value, label]) => ({
          value,
          label,
        }))}
        onChange={(value) => field("status", value as SoundFilters["status"])}
      />
      <SoundFilterSelect
        id="sound-gender"
        label="Gender"
        value={filters.gender}
        allLabel="Any gender"
        options={genders.map((value) => ({ value, label: capitalize(value) }))}
        onChange={(value) => field("gender", value)}
      />
      <SoundFilterSelect
        id="sound-accent"
        label="Accent"
        value={filters.accent}
        allLabel="Any accent"
        options={accents.map((value) => ({ value, label: capitalize(value) }))}
        onChange={(value) => field("accent", value)}
      />
    </div>
  );
}
